/**
 * Holds the product back while a new user's workspace is being seeded.
 *
 * Mounted ABOVE `AppRuntimeProvider`, and that is the whole point. The provider
 * runs every module's `setup()` on mount and the standard `setup()` prefetches
 * `<id>.list`, so a gate placed under it cannot stop user-scoped RPCs from
 * firing against a half-seeded workspace no matter what it renders.
 */
import type { JSX, ReactNode } from "react";

import { SeedingSplash } from "./demo/SeedingSplash";
import { useOnboarding } from "./context";

export function DemoGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const onboarding = useOnboarding();

  if (onboarding.stage === "loading") {
    // Not the splash: a returning user should not see "setting up your
    // workspace" for the length of one profile round-trip.
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary text-content-secondary">
        Loading workspace...
      </div>
    );
  }

  if (onboarding.stage === "demo") {
    const failed = onboarding.seeding.status === "failed";
    return (
      <SeedingSplash
        failed={failed}
        progress={onboarding.seeding.progress}
        actionError={onboarding.actionError}
        actionPending={onboarding.actionPending}
        // Restarting the seed is only the right move when the seed itself
        // failed. When the SEED is healthy and it was the read that failed,
        // `/retry` answers 409 and the person's only button does nothing —
        // re-reading the profile is what actually recovers them.
        onRetry={() => {
          void (failed ? onboarding.retry() : onboarding.refresh());
        }}
      />
    );
  }

  if (onboarding.stage === "unreadable") {
    // Fail OPEN. When the read fails we do not know this person's kind, so
    // blocking would lock a `full` user out over a transient 500. The notice
    // sits beside the app rather than over it — deliberately not a dialog.
    return (
      <>
        {children}
        <div
          data-testid="onboarding-retry-notice"
          className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-edge bg-surface px-4 py-3 text-sm text-content-secondary shadow-lg"
        >
          <span>We couldn&apos;t check your workspace setup.</span>
          <button
            type="button"
            onClick={() => {
              void onboarding.refresh();
            }}
            disabled={onboarding.actionPending !== null}
            className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-on-bright disabled:opacity-50"
          >
            {onboarding.actionPending !== null ? "Trying again…" : "Try again"}
          </button>
        </div>
      </>
    );
  }

  return <>{children}</>;
}
